<template>
    <div class="automation-filter-item-editor">
        <section class="header">
            <AttributeIconPreview :attribute="clone.attribute" />
            <span class="title">
                {{ getAttributeValue() }}
            </span>
            <md-button class="md-icon-button md-mini" @click="onClosed">
                <md-icon>close</md-icon>
            </md-button>
        </section>
        <section class="rules-section">
            <div class="title">{{ $t("chatbotv2.condition") }}</div>
            <div class="d-flex gap-5">
                <strong>Filter Operator By:</strong>
                <CustomPicker
                    v-model="selectedValueType"
                    :items="valueTypes"
                    idKey="id"
                    labelKey="label"
                    title="Select Value Type"
                />
            </div>
            <div class="rules">
                <div
                    class="rule"
                    v-for="rule of getOperators()"
                    :key="rule.value"
                    :class="{ active: rule.value === clone.rule }"
                    @click="() => changeRule(rule.value)"
                >
                    <font-awesome-icon icon="equals" v-if="rule.value === 'equal'" />
                    <font-awesome-icon icon="not-equal" v-else-if="rule.value === 'not_equal'" />
                    <font-awesome-icon icon="less-than" v-else-if="rule.value === 'less_than'" />
                    <font-awesome-icon icon="less-than-equal" v-else-if="rule.value === 'less_or_equal'" />
                    <font-awesome-icon icon="greater-than" v-else-if="rule.value === 'greater_than'" />
                    <font-awesome-icon icon="greater-than-equal" v-else-if="rule.value === 'greater_or_equal'" />
                    {{ $t(`chatbotv2.filters.${rule.value}`) }}
                </div>
            </div>
        </section>
        <section class="value-section" v-if="clone.attribute === 'available_agents'">
            <div class="title">Department</div>
            <span>
                <a-select
                    :value="clone['departmentId']"
                    @change="changeDepartmentId"
                    class="select-department"
                    name="department"
                >
                    <a-select-option :value="''" key="all"> All </a-select-option>
                    <a-select-option v-for="department of subjects" :value="department._id" :key="department._id">
                        {{ getDepartmentTitle(department) }}
                    </a-select-option>
                </a-select>
            </span>
        </section>
        <section
            class="value-section"
            v-if="clone.attribute === 'channelId' && !['empty', 'non_empty'].includes(clone.rule)"
        >
            <div class="title">Channel</div>
            <span>
                <a-select :value="clone.value" @change="(e) => (clone.value = e)" class="select-channel">
                    <a-select-option v-for="channel of channels" :value="channel._id" :key="channel._id"
                        >{{ channel.name }}
                    </a-select-option>
                </a-select>
            </span>
        </section>
        <section class="value-section" v-else-if="clone.attribute === 'segments' && clone.rule !== 'empty'">
            <div class="title">Value</div>
            <SegmentPicker v-model="clone.value" @input="changeSegmentId" />
        </section>
        <section class="value-section" v-else-if="clone.attribute === 'tags._id' && clone.rule !== 'empty'">
            <div class="title">Value</div>
            <TagPicker v-model="clone.value" />
        </section>
        <!--        <section class="value-section" v-else-if="clone.attribute === 'ticket'">-->
        <!--            <div class="title">Ticket</div>-->
        <!--            <TagDialog v-model="clone.value" @input="changeTicket" />-->
        <!--        </section>-->

        <section class="value-section" v-else-if="!['empty', 'non_empty'].includes(clone.rule)">
            <div class="title d-flex gap-5">
                <span>{{ $t("chatbotv2.value") }} </span>

                <div class="btnGroup">
                    <SupsisButton
                        v-for="type in ['static', 'dynamic']"
                        :classNames="inputMode === type ? 'btnGroupItem selected' : 'btnGroupItem '"
                        :primary="inputMode === type"
                        :ghost="inputMode !== type"
                        @click="() => (inputMode = type)"
                        small
                        :key="type"
                    >
                        {{ type }}
                    </SupsisButton>
                </div>
            </div>
            <div v-if="inputMode === 'static'">
                <input
                    v-if="isNumericAttribute()"
                    required="required"
                    type="number"
                    placeholder="Value"
                    :min="-9007199254740991"
                    :max="9007199254740991"
                    :step="1"
                    :class="{ error: isValueValid(), focused: inputFocused }"
                    v-model="clone.value"
                    @focus="inputFocused = true"
                    @blue="inputFocused = false"
                />
                <span v-else-if="isBoolean()">
                    <a-select
                        :value="clone.value + ''"
                        @change="
                            (e) => {
                                clone.value = e;
                            }
                        "
                        class="select-attribute"
                    >
                        <a-select-option
                            :value="booleanValue.value + ''"
                            :key="`${booleanValue.label}-${booleanValue.value}`"
                            v-for="booleanValue of getBooleanValues()"
                            >{{ booleanValue.label }}
                        </a-select-option>
                    </a-select>
                </span>
                <span v-else-if="isSelect()">
                    <a-select
                        :value="clone.value"
                        @change="
                            (e) => {
                                clone.value = e;
                            }
                        "
                        class="select-attribute"
                    >
                        <a-select-option :value="opt.value" :key="opt.value" v-for="opt of getOptions()"
                            >{{ opt.label }}
                        </a-select-option>
                    </a-select>
                </span>
                <input
                    v-else
                    required="required"
                    type="text"
                    :placeholder="$t('chatbotv2.value')"
                    :class="{ error: isValueValid(), focused: inputFocused }"
                    v-model="clone.value"
                    @focus="inputFocused = true"
                    @blue="inputFocused = false"
                />
            </div>
            <div v-else-if="inputMode === 'dynamic'">
                <div class="editor-row customize-editor-minimize">
                    <TiptapEditor placeholder="Value" :show-attributes="true" v-model="clone.value" automation-mode />
                </div>
            </div>

            <a-select
                v-if="clone.attribute === 'department'"
                :value="clone.value"
                @change="(e) => (clone.value = e)"
                class="select-department"
            >
                <a-select-option v-for="department of subjects" :value="department._id" :key="department._id"
                    >{{ getDepartmentTitle(department) }}
                </a-select-option>
            </a-select>
            <a-select
                v-else-if="clone.attribute === 'channelId'"
                :value="clone.value"
                @change="(e) => (clone.value = e)"
                class="select-channel"
            >
                <a-select-option v-for="channel of channels" :value="channel._id" :key="channel._id"
                    >{{ channel.name }}
                </a-select-option>
            </a-select>
        </section>
        <section class="apply">
            <a-button
                type="primary"
                :disabled="!['empty', 'non_empty'].includes(clone.rule) && isValueValid()"
                @click="submit"
            >
                {{ $t("chatbotv2.applyFilter") }}
            </a-button>
        </section>
    </div>
</template>

<script>
import log2 from "../../../../../../../utils/log2";
import { generateRandomId } from "../../../../../../../utils/session";
import _ from "lodash";
import AttributeIconPreview from "../../common/AttributeIconPreview";
import filterOperators from "../../../../utils/filterOperators";
import SegmentPicker from "../../pickers/SegmentPicker.vue";
import TagPicker from "../../pickers/TagPicker.vue";
import TagDialog from "../../../../../../../components/TagDialog.vue";
import { library } from "@fortawesome/fontawesome-svg-core";
import {
    faEquals,
    faGreaterThan,
    faGreaterThanEqual,
    faLessThan,
    faLessThanEqual,
    faNotEqual,
} from "@fortawesome/free-solid-svg-icons";
import { mapActions, mapState } from "vuex";
import AutomationAttributesGenerator from "../../../../utils/AutomationAttributesGenerator";
import CustomPicker from "../../../../../../../components/pickers/CustomPicker.vue";
import SupsisButton from "../../../../../Settings/components/SupsisButton.vue";
import TiptapEditor from "../../../../../Chatbot/pages/dashboard/common/TiptapEditor.vue";

library.add(faEquals, faNotEqual, faLessThan, faLessThanEqual, faGreaterThan, faGreaterThanEqual);
const log = log2("SingleFilterEditor");
export default {
    name: "SingleFilterEditor",
    components: {
        TiptapEditor,
        SupsisButton,
        CustomPicker,
        AttributeIconPreview,
        SegmentPicker,
        TagPicker,
        TagDialog,
    },
    props: {
        value: {
            type: Object,
            required: true,
            default: () => ({
                id: generateRandomId(),
                rule: "greater_or_equal",
                attribute: "email",
                value: 0,
                departmentId: undefined,
            }),
        },
        onClosed: {
            type: Function,
            required: true,
        },
        automationAttributePrefix: {
            type: String,
        },
        allowedContexts: {
            type: Array,
            default: () => ["contact", "block", "event", "system", "custom", "webhook"],
        },
    },
    data() {
        return {
            clone: _.cloneDeep(this.value),
            inputFocused: false,
            window,
            valueTypes: [
                { id: "string", label: "String Operators", description: "Fits for string values" },
                {
                    id: "number",
                    label: "Number Operators",
                    description: "Fits for number values like age, count, etc.",
                },
                { id: "boolean", label: "Boolean Operators", description: "Fits for boolean values like true, false" },
                { id: "date", label: "Date Operators", description: "Fits for date values like created date, etc." },
                {
                    id: "datetime",
                    label: "Datetime Operators",
                    description: "Fits for datetime values like last seen, etc.",
                },
                { id: "time", label: "Time Operators", description: "Fit for time values like last seen, etc." },
                { id: "enum", label: "Enum Operators", description: "Fit for enum values like red|green|blue, etc." },
                { id: "array", label: "Array Operators", description: "Fit for array values like tags, etc." },
                { id: "array_number", label: "Number Array Operators", description: "Fit for numeric array values" },
                { id: "array_string", label: "String Array Operators", description: "Fit for string array values" },
                { id: "array_date", label: "Date Array Operators", description: "Fit for date array values" },
                { id: "array_object", label: "Object Array Operators", description: "Fit for object array values" },
                { id: "array_boolean", label: "Boolean Array Operators", description: "Fit for boolean array values" },
                { id: "object", label: "Json Object Operators", description: "Fit for json object values" },
                { id: "mixed", label: "Mixed Type Operators", description: "Fit for mixed type values" },
            ],
            selectedValueType: "string",
            inputMode: "static",
        };
    },
    computed: {
        ...mapState({
            subjects: (state) => state.subject.subjects.data,
            channels: (state) => state.channel.channels,
            visitorContactAttributes: (state) =>
                (state.contactProperty.data || []).reduce((a, b) => {
                    a[b._id] = b.label;
                    return a;
                }, {}),
            automation: (state) => state.automation.current,
        }),
    },
    methods: {
        ...mapActions({
            getSubjects: "subject/getSubjects",
        }),
        getAllAttributes() {
            const attributeGenerator = AutomationAttributesGenerator.create();
            const attributes = attributeGenerator.list(
                this.automation,
                this.automationAttributePrefix,
                this.allowedContexts
            );
            const flat = (attr) => {
                if (attr.subAttributes) {
                    return [attr, ...attr.subAttributes.flatMap(flat)];
                }
                return [attr];
            };
            return attributes.flatMap(flat);
        },
        changeDepartmentId(departmentId) {
            log("changeDepartmentId", departmentId);
            this.clone.departmentId = departmentId;
            this.clone = _.cloneDeep(this.clone);
            this.$nextTick();
        },
        changeChannelId(channelId) {
            log("changeChannelId", channelId);
            this.clone.channelID = channelId;
            this.clone = _.cloneDeep(this.clone);
            this.$nextTick();
        },
        changeSegmentId(segmentId) {
            log("changeSegmentId", segmentId);
            this.clone.segmentId = segmentId;
            this.clone = _.cloneDeep(this.clone);
            this.$nextTick();
        },
        // changeTicket() {
        //     log("changeTicket", this.clone.tags);
        //     this.clone.tags = this.clone.tags;
        //     this.clone = _.cloneDeep(this.clone);
        //     this.$nextTick();
        // },
        changeRule(rule) {
            this.clone.rule = rule;
        },
        onValueChanged(e) {
            log("onValueChanged", e.target.value);
            this.clone.value = e.target.value;
        },
        getAttributeValue() {
            log("getAttributeValue", this.value.attribute);
            const attributes = this.getAllAttributes();
            const foundAttr = attributes.find((x) => x.value === this.value.attribute);
            return _.get(foundAttr, "value", this.value.attribute);
        },
        getAttributeMeta() {
            const attributes = this.getAllAttributes();
            log("getAttributeMeta", {
                attributes,
                value: this.value.attribute,
            });
            return attributes.find((x) => x.value === this.value.attribute);
        },
        getOperatorName() {
            const foundOperator = filterOperators.find((x) => x.value === this.value.rule);
            log("getOperatorName", foundOperator, this.value.rule, filterOperators);
            return _.get(foundOperator, "label", this.value.rule);
        },
        getOperators() {
            const meta = this.getAttributeMeta();
            const isAvailableOperatorsExist = _.get(meta, "availableOperators", []).length > 0;

            if (this.selectedValueType === "auto" && meta && meta.valueType) {
                if (isAvailableOperatorsExist) {
                    return filterOperators.filter((x) => meta.availableOperators.includes(x.value));
                } else {
                    return filterOperators.filter((x) => x.fitTypes.includes(meta.valueType));
                }
            }
            return filterOperators.filter((x) =>
                this.selectedValueType === "auto" ? true : x.fitTypes.includes(this.selectedValueType)
            );
        },
        isNumericAttribute() {
            const meta = this.getAttributeMeta();
            log("isNumericAttribute: attribute meta", meta);
            return _.get(meta, "valueType") === "number";
        },
        isBoolean() {
            const meta = this.getAttributeMeta();
            log("isBoolean: attribute meta", meta);
            return _.get(meta, "valueType") === "boolean";
        },

        isSelect() {
            const meta = this.getAttributeMeta();
            log("isSelect: attribute meta", meta);
            return _.get(meta, "controlType") === "select";
        },
        getOptions() {
            const meta = this.getAttributeMeta();
            if (this.isSelect()) {
                const options = _.get(meta, "options", []) || [];
                if (typeof options === "function") {
                    return options();
                }
                return options;
            }
        },
        getBooleanValues() {
            return [
                {
                    label: "True",
                    value: "true",
                },
                {
                    label: "False",
                    value: "false",
                },
            ];
        },
        isValueValid() {
            return ["empty", "non_empty"].includes(this.clone.rule) || _.isEmpty(this.clone.value);
        },
        submit() {
            this.$emit("input", this.clone);
            this.onClosed();
        },
        getDepartmentTitle(department) {
            const langs = Object.keys(department.languageContent);
            const targetLang = langs.includes(this.$i18n.locale) ? this.$i18n.locale : langs[0];
            return _.get(department, `languageContent.${targetLang}.name`);
        },
    },
    mounted() {
        console.log("SingleFilterEditor mounted", this.clone);
        if (["available_agents", "department", "segments", "ticket"].includes(this.clone.attribute)) {
            if (this.subjects.length === 0) {
                this.getSubjects();
            }
        }
    },
    watch: {},
};
</script>

<style lang="scss">
.automation-filter-item-editor {
    grid-row-gap: 20px;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    max-width: 90vw;
    padding: 20px;
    width: 500px;

    .select-department,
    .select-channel,
    .select-attribute {
        width: 100%;
    }

    .btnGroup {
        display: flex;
        border: 1px solid var(--color-border-default);
        border-radius: 6px;
        background: var(--color-bg-neutral-default);
        margin: 2px 10px;
        padding: 2px;

        .btnGrupItem {
            &.selected {
                background: var(--color-bg-oncomponent-default);
                box-shadow: var(--shadow-interaction-200);
            }
        }

        .supsis-button {
            border: none !important;
            font-size: 13px;
            height: 25px;
        }
    }

    section {
        .title {
            margin-bottom: 5px;
            color: #22272d;
            font-weight: 600;
            font-size: 16px;
            line-height: 20px;
        }
    }

    section.header {
        align-items: center;
        display: grid;
        grid-template-columns: auto 1fr auto;
        gap: 15px;

        .title {
            margin-bottom: 0px;
        }

        .material-icons {
            color: #006cff;
            font-size: 25px;
            margin-right: 10px;
        }
    }

    section.rules-section {
        .rules {
            display: flex;
            gap: 5px;
            flex-wrap: wrap;
            margin: -5px;

            .rule {
                &.active,
                &:hover {
                    background: #006cff;
                    color: #fff;
                    cursor: default;
                }

                align-items: center;
                background: #f0f4f7;
                border-radius: 6px;
                display: flex;
                font-size: 15px;
                font-weight: 600;
                line-height: 19px;
                margin: 5px;
                padding: 10px 15px;
                transition: all 0.3s;
                cursor: pointer;

                i,
                svg {
                    font-size: 10px;
                    margin-right: 7px;
                }
            }
        }
    }

    section.value-section {
        .editor-row {
            width: 100%;

            .editor-row-title {
                font-weight: bold;
                font-size: 16px;
            }

            .editable-editor {
                background: #eaeaeadb !important;
            }
        }

        input {
            background: #fff;
            border: 1px solid #c6cdd4;
            border-radius: 6px;
            color: #22272d;
            display: block;
            font-family: Source Sans Pro, sans-serif;
            font-weight: 400;
            height: 37px;
            line-height: 35px;
            padding: 0 15px;
            transition: border 0.3s;
            width: 100%;
            font-size: 15px;

            &.error {
                border-color: #fb6e6e;
            }

            &.focused,
            &:hover {
                border-color: #008aff;
            }
        }
    }

    section.apply {
        .ant-btn {
            min-width: 80px;
            color: #fff;
            height: 37px;
            line-height: 35px;
            width: 100%;
            border-radius: 4px;
            display: inline-block;
            font-family: Source Sans Pro, sans-serif;
            font-weight: 600;
            padding: 0 10px;
            text-align: center;
            cursor: pointer;
            transition: all 0.3s;

            &[disabled] {
                background: #f5f8fb;
                border: 1px solid #f5f8fb;
                color: #919ead;
                cursor: not-allowed;
            }
        }
    }
}
</style>
